import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  closeCurrentDialog,
  closeDialog,
  DataStatus,
  DialogFooter,
  DropdownItem,
  Flag,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Label,
  openDialog,
  openDropdown,
  Segment,
  Separator,
  showNotification,
  Tooltip,
} from 'platform/components';
import {Box, Grid, GridItem, Hide, HStack, Show, Space, VStack} from 'platform/foundation';

import {useEffect, useRef, useState} from 'react';
import {DeepPartial, UseFormReturn} from 'react-hook-form';

import {defaultTo, isNil, isNotNil, not} from 'ramda';
import {isNilOrEmpty, isString} from 'ramda-adjunct';

import {useGetManufacturersQuery, useGetTreeFolderQuery} from '@dms/api/core';
import {TenantResponseBody} from '@dms/api/shared';
import {
  GetReceiveNoteItemNewResponse,
  GetReceiveNoteItemResponse,
  GetWarehouseResponse,
  PatchReceiveNoteItemManualRequest,
  PostArticleRequest,
  PostReceiveNoteBasketItemRequest,
  PutSupplierArticlePromptValidationRequest,
  useGetArticleCategoryListQuery,
  useGetDispensingUnitPresetsQuery,
  useGetHandlingUnitsQuery,
  useGetSupplierDiscountGroupsQuery,
  useGetSuppliersQuery,
  useGetWarehousesQuery,
  useLazyGetArticleIdByManufacturerNumberAndManufacturerIdQuery,
  useLazyGetReceiveNoteItemNewQuery,
  useLazyGetReceiveNoteItemQuery,
  useLazyGetReceiveNoteItemRefreshedQuery,
  usePatchReceiveNoteItemManualMutation,
  usePostArticleMutation,
  usePutSupplierArticlePromptValidationMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {
  DEFAULT_CURRENCY,
  getOptionsFromManufacturers,
  getOptionsFromWarehouses,
  handleApiError,
  logError,
  refreshDmsDatagrid,
  usePermissions,
} from '@dms/shared';
import {
  ArticleCreate,
  ArticleCreateDefaultValues,
  ArticleCreateTreeFolderPath,
  ArticleState,
  CreatedArticleData,
  getOptionsFromHandlingUnits,
} from '@dms/shared-aftersales';
import {getArticleDetailCardPrices, SUPPLIER_ARTICLE_PROMPT} from '@dms/warehouse/shared';
import {SupplierArticlePrompt} from '@dms/warehouse/shared-components';

import {
  AllOrNone,
  generateHashFromObjects,
  Nullish,
  RequiredTestIdProps,
  suffixTestId,
  useOnMount,
} from 'shared/web';

import {RECEIVE_NOTE_ITEM_DIALOG, REPLACE_ARTICLE_DIALOG} from '../constants/receiveNoteItemDialog';
import {ItemRowData} from '../types/ItemRowData';
import {ArticleDetailCard} from './ArticleDetailCard';
import {AvailabilitySegment} from './AvailabilitySegment';
import {DeliveryItemDetailCard} from './DeliveryItemDetailCard';
import {DeliveryItemForm} from './DeliveryItemForm';
import {useReceiveNoteItemFormRecalculation} from './hooks/useReceiveNoteItemFormRecalculation';
import {ReceiveNoteItemForm} from './ReceiveNoteItemForm';
import {ReplaceArticleModal} from './ReplaceArticleModal';
import {RequestsSegment} from './RequestsSegment';
import {SupplierArticleForm} from './SupplierArticleForm';
import {SupplierOrdersSegment} from './SupplierOrdersSegment';
import {ReceiveNoteItemDetails} from './types/ReceiveNoteItemDetails';
import {
  ReceiveNoteItemDetailSchema,
  ReceiveNoteItemFormType,
} from './types/ReceiveNoteItemDetailSchema';

// There is a delay on the backend between replacing the article and reflecting this change in the data grid
const WAREHOUSE_AVAILABILITY_REFRESH_DELAY_MS = 1000;

enum Segments {
  AVAILABILITY = 'availability',
  REQUESTS = 'requests',
  SUPPLIER_ORDERS = 'supplierOrders',
}

type Mode = 'creating' | 'editing' | 'adding';

type WarehouseType =
  | GetWarehouseResponse
  | {
      id: string;
      warehouseNumber?: string;
      name: string;
      authorizationProfileId: string;
      defaultManufacturerId?: string;
      currency?: string;
    };

export type ReceiveNoteItemDetailProps = {
  receiveNoteId: string | Nullish;
  authorizationProfileId: string | Nullish;
  itemRowData?: ItemRowData | Nullish;
  warehouse: WarehouseType | Nullish;
  tenant: TenantResponseBody | Nullish;
  state: 'PENDING' | 'COMPLETED' | Nullish;
  receiveNoteItemId?: string;
  defaultValues?: DefaultValues;
  mode: Mode;
  isFromReceiveNote?: boolean;
  receiveNoteType?: string | Nullish;
  isReplaceArticleAlwaysDisabled?: boolean;
} & RequiredTestIdProps &
  BasketProps;

type BasketProps = AllOrNone<{
  context?: 'BASKET';
  onAddToBasket?: (requestBody: PostReceiveNoteBasketItemRequest['body']) => Promise<void>;
  isAddingToBasket?: boolean;
}>;

type DefaultValues = {
  catalogNumber?: string;
  manufacturerId?: string;
  warehouseId?: string;
  name?: string;
  activeFolderId?: string;
};

export type HandleCheckItemConfig = {
  manufacturerId: string;
  catalogNumber: string;
  warehouseId: string;
};

export function ReceiveNoteItemDetail(props: ReceiveNoteItemDetailProps) {
  const [activeSegment, setActiveSegment] = useState<Segments>(Segments.AVAILABILITY);

  const [canEditReceiveNote] = usePermissions({
    permissionKeys: ['receiveNoteEdit'],
  });

  const [leafId, setLeafId] = useState<string | Nullish>(null);
  const [leafIdErrorMessage, setLeafIdErrorMessage] = useState<string | undefined>(undefined);
  const [lastCheckedState, setLastCheckedState] = useState<HandleCheckItemConfig | null>(null);

  const [articleState, setArticleState] = useState<ArticleState | null>(null);
  const [replaceArticleId, setReplaceArticleId] = useState<string | null>(null);

  const [receiveNoteItem, setReceiveNoteItem] = useState<GetReceiveNoteItemResponse | Nullish>(
    null
  );

  const [receiveNoteItemNew, setReceiveNoteItemNew] = useState<
    GetReceiveNoteItemNewResponse | Nullish
  >(null);

  const [receiveNoteItemDetails, setReceiveNoteItemDetails] = useState<ReceiveNoteItemDetails>();

  const {data: warehouses, isLoading: isWarehouseLoading} = useGetWarehousesQuery();

  const {data: suppliers, isLoading: areSuppliersLoading} = useGetSuppliersQuery();

  const {data: manufacturers, isLoading: areManufacturersLoading} = useGetManufacturersQuery();

  const {data: handlingUnits, isLoading: isLoadingHandlingUnits} = useGetHandlingUnitsQuery(
    undefined,
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const {data: dispensingUnitPresets, isLoading: isDispensingUnitPresetsLoading} =
    useGetDispensingUnitPresetsQuery();

  const {data: categories} = useGetArticleCategoryListQuery();

  const [getReceiveNoteItem, {isLoading: isReceiveNoteItemLoading}] =
    useLazyGetReceiveNoteItemQuery();

  const [getReceiveNoteItemNew, {isFetching: isNewReceiveNoteItemLoading}] =
    useLazyGetReceiveNoteItemNewQuery();

  const [getReceiveNoteItemRefreshed, {isFetching: isRefreshingDetails}] =
    useLazyGetReceiveNoteItemRefreshedQuery();

  const [getArticleIdByManufacturerNumberAndManufacturerId, {isFetching: isCheckingForArticle}] =
    useLazyGetArticleIdByManufacturerNumberAndManufacturerIdQuery();

  const [putSupplierArticlePromptValidation] = usePutSupplierArticlePromptValidationMutation();

  const [patchReceiveNoteItemManual, {isLoading: isPatchReceiveNoteItemLoading}] =
    usePatchReceiveNoteItemManualMutation({fixedCacheKey: 'edit-receive-note-item'});

  const [createArticle, {isLoading: isCreateArticleLoading}] = usePostArticleMutation();

  const defaultHandlingUnit = handlingUnits?.find((handlingUnit) => handlingUnit.isDefault);

  const articleId = receiveNoteItemNew?.article?.id ?? receiveNoteItem?.article?.id;

  const supplierId =
    receiveNoteItemNew?.supplierArticle?.supplierId ?? receiveNoteItem?.supplierArticle?.supplierId;

  const manufacturerNumber =
    receiveNoteItemNew?.article?.manufacturerNumber ?? receiveNoteItem?.article?.manufacturerNumber;

  const manufacturerId =
    receiveNoteItemNew?.article?.manufacturerId ?? receiveNoteItem?.article?.manufacturerId;

  const deliveryNoteQuantity =
    receiveNoteItemNew?.deliveryNoteItem?.quantity ?? receiveNoteItem?.deliveryNoteItem?.quantity;

  const deliveryNoteUnitPrice =
    receiveNoteItemNew?.deliveryNoteItem?.purchasePrice ??
    receiveNoteItem?.deliveryNoteItem?.purchasePrice;

  const deliveryNoteTotalPrice =
    receiveNoteItemNew?.deliveryNoteItem?.purchaseValue ??
    receiveNoteItem?.deliveryNoteItem?.purchaseValue;

  const warehouseUnitId =
    receiveNoteItemNew?.article?.handlingUnit ?? receiveNoteItem?.article?.handlingUnit;

  const articleHandlingUnitName = handlingUnits?.find(
    (handlingUnit) => handlingUnit.id === warehouseUnitId
  )?.name;

  const articlePrices = getArticleDetailCardPrices(
    receiveNoteItemNew?.article ?? receiveNoteItem?.article
  );

  const warehouseName = warehouses?.find(
    (warehouse) => warehouse?.id === props.warehouse?.id
  )?.name;

  const defaultCurrency = props.tenant?.currency ?? DEFAULT_CURRENCY;
  const currency = defaultTo(defaultCurrency, props.warehouse?.currency);

  const isCreating = props.mode === 'creating';
  const isEditing = props.mode === 'editing';
  const isEditingDisabled = isEditing && props.state === 'COMPLETED';
  const doesExist = articleState === 'doesExist';
  const doesNotExist = articleState === 'doesNotExist';
  const hasBeenCreated = articleState === 'hasBeenCreated';
  const isChecked = doesExist || doesNotExist || hasBeenCreated;
  const isLoadingMainContent = isCheckingForArticle || isNewReceiveNoteItemLoading;
  const isNotChecked = not(articleState);
  const shouldCardsBeExpanded = isChecked || isEditing;
  const defaultCategoryId = categories?.categories?.find((category) => category.isDefault)?.id;
  const isArticleReplacement =
    props.isReplaceArticleAlwaysDisabled !== true &&
    (receiveNoteItemNew?.receiveNoteItem.isArticleReplaceAllowed ??
      receiveNoteItem?.receiveNoteItem.isArticleReplaceAllowed);

  const isArticleReplaced = receiveNoteItem?.deliveryNoteItem.isArticleReplaced;

  const {data: defaultTreeFolder, isLoading: isDefaultTreeFolderLoading} = useGetTreeFolderQuery(
    {contextTarget: 'spare-parts-catalog', contextId: defaultCategoryId ?? ''},
    {skip: isNil(defaultCategoryId)}
  );

  const {data: supplierDiscountGroups, isLoading: isLoadingSupplierDiscountGroups} =
    useGetSupplierDiscountGroupsQuery(
      {
        supplierId: supplierId as string,
        authorizationProfileId: props.authorizationProfileId as string,
      },
      {skip: isNilOrEmpty(supplierId) || isNilOrEmpty(props.authorizationProfileId)}
    );

  const defaultValues: DeepPartial<ReceiveNoteItemFormType> = {
    article: {
      catalogNumber: isCreating
        ? props.defaultValues?.catalogNumber
        : isArticleReplaced
          ? receiveNoteItem?.article.manufacturerNumber
          : props.itemRowData?.catalogNumber,
      manufacturerId: isCreating
        ? props.defaultValues?.manufacturerId
        : props.itemRowData?.manufacturerId,
      warehouseId: isCreating ? props.defaultValues?.warehouseId : props.warehouse?.id,
      name: isCreating ? props.defaultValues?.name : props.itemRowData?.name,
      handlingUnit: warehouseUnitId ?? defaultHandlingUnit?.id,
      supplierId: props.itemRowData?.supplierId, // used only for price list tab flow
    },
    receiveNoteItem: {
      quantity: defaultTo(1, receiveNoteItem?.receiveNoteItem?.quantity),
      unitPrice: defaultTo(0, receiveNoteItem?.receiveNoteItem?.purchasePrice),
      totalPrice: defaultTo(0, receiveNoteItem?.receiveNoteItem?.purchaseValue),
      vatType: defaultTo('S', receiveNoteItem?.receiveNoteItem?.vatType),
    },
    deliveryItem: {
      quantityVerified: defaultTo(1, receiveNoteItem?.deliveryNoteItem?.quantityVerified),
      unitPrice: defaultTo(0, receiveNoteItem?.deliveryNoteItem?.purchasePrice),
      totalPrice: defaultTo(0, receiveNoteItem?.deliveryNoteItem?.purchaseValue),
      vatType: defaultTo('S', receiveNoteItem?.deliveryNoteItem?.vatType),
      purchasePriceVerified: defaultTo(0, receiveNoteItem?.deliveryNoteItem?.purchasePriceVerified),
      stockValueVerified: defaultTo(0, receiveNoteItem?.deliveryNoteItem?.stockValueVerified),
    },
    supplierArticle: {
      supplierQuantity: defaultTo(1, receiveNoteItem?.supplierArticle?.supplierQuantity),
      supplierUnitId: receiveNoteItem?.supplierArticle?.supplierUnitId,
      warehouseQuantity: defaultTo(1, receiveNoteItem?.supplierArticle?.warehouseQuantity),
      warehouseUnitId: receiveNoteItem?.article?.handlingUnit,
      supplierOrderingNumber: receiveNoteItem?.supplierArticle?.supplierOrderingNumber,
      supplierBulkPackageQuantity: defaultTo(
        1,
        receiveNoteItem?.supplierArticle?.supplierBulkPackageQuantity
      ),
      supplier: suppliers?.find(
        (supplier) => supplier.id === receiveNoteItem?.supplierArticle?.supplierId
      )?.name,
      supplierDiscountGroup: receiveNoteItem?.supplierArticle?.supplierDiscountGroupCode,
    },
  };

  const {handleChange, isCalculateLoading, setPrevFormValues} =
    useReceiveNoteItemFormRecalculation();

  const formApiRef = useRef<UseFormReturn<ReceiveNoteItemFormType> | null>(null);

  useOnMount(() => {
    if (props.mode === 'editing') {
      handleGetReceiveNoteItemEdit();
      return;
    }

    if (
      props.mode === 'adding' &&
      isString(props.itemRowData?.manufacturerId) &&
      isString(props.itemRowData?.catalogNumber) &&
      isString(props.warehouse?.id)
    ) {
      handleCheckItem({
        manufacturerId: props.itemRowData?.manufacturerId,
        catalogNumber: props.itemRowData?.catalogNumber,
        warehouseId: props.warehouse?.id,
      });
      return;
    }

    if (
      props.mode === 'creating' &&
      isString(props.defaultValues?.manufacturerId) &&
      isString(props.defaultValues?.catalogNumber) &&
      isString(props.defaultValues?.warehouseId)
    ) {
      handleCheckItem({
        manufacturerId: props.defaultValues?.manufacturerId,
        catalogNumber: props.defaultValues?.catalogNumber,
        warehouseId: props.defaultValues?.warehouseId,
      });
      return;
    }
  });

  useEffect(() => {
    if (isNotNil(leafId) || isNil(defaultTreeFolder)) {
      return;
    }

    setLeafId(defaultTreeFolder?.treeFolder?.id);
  }, [defaultTreeFolder, leafId]);

  useEffect(() => {
    if (isNil(replaceArticleId)) {
      return;
    }

    handleGetReceiveNoteItemReplaced(replaceArticleId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replaceArticleId]);

  const handleGetReceiveNoteItemEdit = async () =>
    await getReceiveNoteItem({
      receiveNoteId: props.receiveNoteId as string,
      itemId: props.receiveNoteItemId as string,
    })
      .unwrap()
      .then((receiveNoteItem) => {
        // Set the prevFormValues now, we need them for proper recalculation of prices
        handleMapPrevFormValues(receiveNoteItem);
        setReceiveNoteItem(receiveNoteItem);
        setReceiveNoteItemDetails({
          deliveryItemDetail: {
            manufacturerNumber: receiveNoteItem?.deliveryNoteItem?.manufacturerNumber,
            manufacturerId: receiveNoteItem?.deliveryNoteItem?.manufacturerId,
            supplierNumber: receiveNoteItem?.deliveryNoteItem?.supplierNumber,
            name: receiveNoteItem?.deliveryNoteItem?.name,
            description: receiveNoteItem?.deliveryNoteItem?.description,
          },
          articleDetail: {
            warehouseId: receiveNoteItem?.article?.warehouseId,
            articleId: receiveNoteItem?.article?.id,
            name: receiveNoteItem?.article?.name,
            manufacturerNumber: receiveNoteItem?.article?.manufacturerNumber,
            manufacturerId: receiveNoteItem?.article?.manufacturerId,
            storageLocation: receiveNoteItem?.article?.storageLocation,
            leafId: receiveNoteItem?.article?.treeFolder?.leafId,
          },
        });
      })
      .then(() => setArticleState('doesExist'))
      .catch(handleApiError);

  const handleGetReceiveNoteItem = async (articleId: string, wasCreated: boolean) => {
    if (isNilOrEmpty(props.receiveNoteId)) {
      return;
    }

    const request = {
      receiveNoteId: props.receiveNoteId as string,
      articleId,
    };

    await getReceiveNoteItemNew(request)
      .unwrap()
      .then((result) => {
        handleArticleState(result, wasCreated);
        resetFormValues(result);
        setReceiveNoteItemDetails({
          deliveryItemDetail: {
            manufacturerNumber: result?.deliveryNoteItem?.manufacturerNumber,
            manufacturerId: result?.deliveryNoteItem?.manufacturerId,
            supplierNumber: result?.deliveryNoteItem?.supplierNumber,
            name: result?.deliveryNoteItem?.name,
            description: result?.deliveryNoteItem?.description,
          },
          articleDetail: {
            warehouseId: result?.article?.warehouseId,
            articleId: result?.article?.id,
            name: result?.article?.name,
            manufacturerNumber: result?.article?.manufacturerNumber,
            manufacturerId: result?.article?.manufacturerId,
            storageLocation: result?.article?.storageLocation,
            leafId: result?.article?.treeFolder?.leafId,
          },
        });
      })
      .catch(handleApiError);
  };

  const handleGetReceiveNoteItemReplaced = async (articleId: string) => {
    if (isNilOrEmpty(props.receiveNoteId)) {
      return;
    }

    const request = {
      receiveNoteId: props.receiveNoteId as string,
      articleId,
      receiveNoteItemId: props.receiveNoteItemId as string,
    };

    await getReceiveNoteItemNew(request)
      .unwrap()
      .then((result) => {
        // When replacing an article, we want to:
        // 1. Update the article details display (ArticleDetailCard)
        // 2. Keep the existing receiveNoteItem and deliveryItem form values unchanged
        // 3. Only update SupplierArticle form fields with new article's supplier mapping
        // This preserves user-entered quantities and prices while updating supplier-specific data

        // Update ArticleDetailCard data
        setReceiveNoteItemDetails(
          (prevDetails) =>
            ({
              ...prevDetails,
              articleDetail: {
                warehouseId: result?.article?.warehouseId,
                articleId: result?.article?.id,
                name: result?.article?.name,
                manufacturerNumber: result?.article?.manufacturerNumber,
                manufacturerId: result?.article?.manufacturerId,
                storageLocation: result?.article?.storageLocation,
                leafId: result?.article?.treeFolder?.leafId,
              },
            }) as ReceiveNoteItemDetails
        );

        // Update receiveNoteItemNew so manufacturerNumber and manufacturerId reflect the new article
        setReceiveNoteItemNew(result);

        // Only update SupplierArticle fields, keep receiveNoteItem and deliveryItem unchanged
        resetAfterArticleReplacement(result);

        showNotification.success(
          i18n.t('entity.warehouse.notification.articleReplacedSuccessfully')
        );
        setTimeout(
          () => refreshDmsDatagrid('warehouse-availability'),
          WAREHOUSE_AVAILABILITY_REFRESH_DELAY_MS
        );
      })
      .catch(handleApiError);
  };

  const handleCheckItem = async (config: HandleCheckItemConfig) => {
    const articleIdResponse = await getArticleIdByManufacturerNumberAndManufacturerId({
      warehouseId: config.warehouseId,
      manufacturerNumber: config.catalogNumber,
      manufacturerId: config.manufacturerId,
    })
      .unwrap()
      .then((response) => {
        setLastCheckedState(config);
        return response;
      })
      .catch(handleApiError);

    if (!articleIdResponse?.articleId) {
      setArticleState('doesNotExist');
      setReceiveNoteItemNew(null);

      if (articleIdResponse?.name) {
        formApiRef.current?.setValue('article.name', articleIdResponse.name);
      }
      if (articleIdResponse?.measurementUnitId) {
        formApiRef.current?.setValue('article.handlingUnit', articleIdResponse.measurementUnitId);
      }
      return;
    }

    await handleGetReceiveNoteItem(articleIdResponse.articleId, false);
  };

  const handleArticleState = (
    receiveNoteItem: GetReceiveNoteItemNewResponse | Nullish,
    wasCreated: boolean
  ) => {
    if (isNil(receiveNoteItem)) {
      return setArticleState('doesNotExist');
    }

    if (wasCreated) {
      setArticleState('hasBeenCreated');
      handleMapPrevFormValues(receiveNoteItem);
      setReceiveNoteItemNew(receiveNoteItem);
      return;
    }

    setArticleState('doesExist');
    handleMapPrevFormValues(receiveNoteItem);
    setReceiveNoteItemNew(receiveNoteItem);
  };

  const handleRefreshArticle = async (formApi: UseFormReturn<ReceiveNoteItemFormType>) => {
    await getReceiveNoteItemRefreshed({
      receiveNoteId: props.receiveNoteId as string,
      articleId: articleId as string,
    })
      .unwrap()
      .then((receiveNoteItemRefreshed) => {
        mapWarehouseUnit(receiveNoteItemRefreshed.article.handlingUnit, formApi);
        setReceiveNoteItemDetails({
          deliveryItemDetail: {
            manufacturerNumber: receiveNoteItemRefreshed?.deliveryNoteItem?.manufacturerNumber,
            manufacturerId: receiveNoteItemRefreshed?.deliveryNoteItem?.manufacturerId,
            supplierNumber: receiveNoteItemRefreshed?.deliveryNoteItem?.supplierNumber,
            name: receiveNoteItemRefreshed?.deliveryNoteItem?.name,
            description: receiveNoteItemRefreshed?.deliveryNoteItem?.description,
          },
          articleDetail: {
            warehouseId: receiveNoteItemRefreshed?.article?.warehouseId,
            articleId: receiveNoteItemRefreshed?.article?.id,
            name: receiveNoteItemRefreshed?.article?.name,
            manufacturerNumber: receiveNoteItemRefreshed?.article?.manufacturerNumber,
            manufacturerId: receiveNoteItemRefreshed?.article?.manufacturerId,
            storageLocation: receiveNoteItemRefreshed?.article?.storageLocation,
            leafId: receiveNoteItemRefreshed?.article?.treeFolder?.leafId,
          },
        });
      })
      .catch(handleApiError);
  };

  const onFormSubmitHandle: FormSubmitHandler<ReceiveNoteItemFormType> = async (formValues) => {
    if (articleState === 'doesNotExist') {
      if (!leafId) {
        setLeafIdErrorMessage(i18n.t('general.validations.fieldIsRequired'));
        return;
      }

      const requestData: PostArticleRequest = {
        warehouseId: formValues.article.warehouseId,
        body: {
          manufacturerNumber: formValues.article.catalogNumber,
          manufacturerId: formValues.article.manufacturerId,
          name: formValues.article.name,
          storageLocation: formValues.article.storageLocation,
          handlingUnit: formValues.article.handlingUnit,
          dispensingUnit: formValues.article.dispensingUnit,
          treeFolder: {leafId},
          supplierId: formValues.article.supplierId, // used only for price list tab flow
        },
      };

      handleCreateNewArticle(requestData);

      return;
    }

    const supplierArticlePromptValidationRequestPayload: PutSupplierArticlePromptValidationRequest =
      {
        articleId: replaceArticleId ?? (articleId as string),
        supplierId: supplierId as string,
        manufacturerNumber: formValues.article.catalogNumber as string,
        supplierOrderingNumber: formValues.supplierArticle.supplierOrderingNumber,
        supplierToWarehouseMapping: {
          supplierQuantity: formValues.supplierArticle.supplierQuantity,
          supplierUnitId: formValues.supplierArticle.supplierUnitId,
          warehouseQuantity: formValues.supplierArticle.warehouseQuantity,
          supplierBulkPackageQuantity: formValues.supplierArticle.supplierBulkPackageQuantity,
        },
      };

    await putSupplierArticlePromptValidation(supplierArticlePromptValidationRequestPayload)
      .unwrap()
      .then((result) => {
        if (not(result.isChanged)) {
          return handleSubmit(formValues);
        }

        openDialog(
          <SupplierArticlePrompt
            onDontSave={() => handleSubmit(formValues, false)}
            onSave={() => handleSubmit(formValues, true)}
            data-testid={suffixTestId('supplierArticlePrompt', props)}
          />,
          {
            id: SUPPLIER_ARTICLE_PROMPT,
            size: 'small',
            'data-testid': suffixTestId('dialogs.supplierArticlePrompt', props),
          }
        );
      })
      .catch(handleApiError);
  };

  const handleSubmit = async (
    formValues: ReceiveNoteItemFormType,
    shouldUpdateMapping?: boolean
  ) => {
    if (props.context === 'BASKET') {
      return handleBasketSubmit(formValues, shouldUpdateMapping);
    }

    if (isCreating) {
      const requestBody: PostReceiveNoteBasketItemRequest['body'] = {
        receiveNoteId: props.receiveNoteId as string,
        articleId: articleId as string,
        quantity: formValues.receiveNoteItem.quantity,
        purchasePrice: formValues.receiveNoteItem.unitPrice,
        stockValue: formValues.receiveNoteItem.totalPrice,
        vatType: formValues.receiveNoteItem.vatType,
        supplierArticle: {
          supplierQuantity: formValues.supplierArticle.supplierQuantity,
          supplierUnitId: formValues.supplierArticle.supplierUnitId,
          warehouseQuantity: formValues.supplierArticle.warehouseQuantity,
          supplierOrderingNumber: formValues.supplierArticle.supplierOrderingNumber,
          supplierBulkPackageQuantity: formValues.supplierArticle.supplierBulkPackageQuantity,
          isUpdateMapping: shouldUpdateMapping ?? false,
        },
      };

      if (isNil(props.onAddToBasket)) {
        return logError('Missing onAddToBasket');
      }

      return await props.onAddToBasket(requestBody);
    }

    const receiveNoteItemRequest: PatchReceiveNoteItemManualRequest = {
      receiveNoteItemId: props.receiveNoteItemId as string,
      body: {
        quantity: formValues.receiveNoteItem.quantity,
        purchasePrice: formValues.receiveNoteItem.unitPrice,
        stockValue: formValues.receiveNoteItem.totalPrice,
        vatType: formValues.receiveNoteItem.vatType,
        deliveryItem: {
          quantityVerified: formValues.deliveryItem.quantityVerified,
          unitPrice: formValues.deliveryItem.purchasePriceVerified,
          stockValue: formValues.deliveryItem.stockValueVerified,
          vatType: formValues.deliveryItem.vatType,
        },
        supplierArticle: {
          supplierQuantity: formValues.supplierArticle.supplierQuantity,
          supplierUnitId: formValues.supplierArticle.supplierUnitId,
          warehouseQuantity: formValues.supplierArticle.warehouseQuantity,
          supplierOrderingNumber: formValues.supplierArticle.supplierOrderingNumber,
          supplierBulkPackageQuantity: formValues.supplierArticle.supplierBulkPackageQuantity,
          isUpdateMapping: shouldUpdateMapping ?? false,
        },
        replacedArticle: {
          articleId: replaceArticleId ?? (articleId as string),
        },
      },
    };

    await patchReceiveNoteItemManual(receiveNoteItemRequest)
      .unwrap()
      .then(() =>
        showNotification.success(i18n.t('page.warehouse.notification.receiveNoteItemUpdated'))
      )
      .then(() => closeDialog(RECEIVE_NOTE_ITEM_DIALOG))
      .then(() => refreshDmsDatagrid('receive-note-item'))
      .catch(handleApiError);
  };

  const handleBasketSubmit = async (
    formValues: ReceiveNoteItemFormType,
    shouldUpdateMapping?: boolean
  ) => {
    const requestBody: PostReceiveNoteBasketItemRequest['body'] = {
      receiveNoteId: props.receiveNoteId as string,
      articleId: articleId as string,
      quantity: formValues.receiveNoteItem.quantity,
      purchasePrice: formValues.receiveNoteItem.unitPrice,
      stockValue: formValues.receiveNoteItem.totalPrice,
      vatType: formValues.receiveNoteItem.vatType,
      supplierArticle: {
        supplierQuantity: formValues.supplierArticle.supplierQuantity,
        supplierUnitId: formValues.supplierArticle.supplierUnitId,
        warehouseQuantity: formValues.supplierArticle.warehouseQuantity,
        supplierOrderingNumber: formValues.supplierArticle.supplierOrderingNumber,
        supplierBulkPackageQuantity: formValues.supplierArticle.supplierBulkPackageQuantity,
        isUpdateMapping: shouldUpdateMapping ?? false,
      },
    };

    if (isNil(props.onAddToBasket)) {
      return logError('Missing onAddToBasket');
    }

    await props.onAddToBasket(requestBody);
  };

  const handleMapPrevFormValues = (
    data: GetReceiveNoteItemResponse | GetReceiveNoteItemNewResponse | Nullish
  ) => {
    const mappedValues = {
      article: {
        name: data?.article.name,
        manufacturerId: data?.article.manufacturerId,
        catalogNumber: data?.article.manufacturerNumber,
        warehouseId: data?.article.warehouseId,
        storageLocation: data?.article.storageLocation,
        handlingUnit: data?.article.handlingUnit,
        dispensingUnit: data?.article.dispensingUnit,
        supplierId: data?.article.supplierId,
      },
      receiveNoteItem: {
        quantity: defaultTo(1, data?.receiveNoteItem?.quantity),
        unitPrice: defaultTo(0, data?.receiveNoteItem?.purchasePrice),
        totalPrice: defaultTo(0, data?.receiveNoteItem?.purchaseValue),
        vatType: defaultTo('S', data?.receiveNoteItem?.vatType),
      },
      deliveryItem: {
        quantityVerified: defaultTo(1, data?.deliveryNoteItem?.quantityVerified),
        unitPrice: defaultTo(0, data?.deliveryNoteItem?.purchasePrice),
        totalPrice: defaultTo(0, data?.deliveryNoteItem?.purchaseValue),
        vatType: defaultTo('S', data?.deliveryNoteItem?.vatType),
        purchasePriceVerified: defaultTo(0, data?.deliveryNoteItem?.purchasePriceVerified),
        stockValueVerified: defaultTo(0, data?.deliveryNoteItem?.stockValueVerified),
      },
      supplierArticle: {
        supplierQuantity: defaultTo(1, data?.supplierArticle?.supplierQuantity),
        supplierUnitId: data?.supplierArticle?.supplierUnitId,
        warehouseQuantity: defaultTo(1, data?.supplierArticle?.warehouseQuantity),
        warehouseUnitId: data?.article?.handlingUnit,
        supplierOrderingNumber: data?.supplierArticle?.supplierOrderingNumber,
        supplierBulkPackageQuantity: defaultTo(
          1,
          data?.supplierArticle?.supplierBulkPackageQuantity
        ),
        supplier: suppliers?.find((supplier) => supplier.id === data?.supplierArticle?.supplierId)
          ?.name,
        supplierDiscountGroup: data?.supplierArticle?.supplierDiscountGroupCode,
      },
    };
    setPrevFormValues(mappedValues);
  };

  const resetFormValues = (data: GetReceiveNoteItemNewResponse | Nullish) => {
    formApiRef?.current?.reset({
      article: {
        name: data?.article.name,
        manufacturerId: data?.article.manufacturerId,
        catalogNumber: data?.article.manufacturerNumber,
        warehouseId: data?.article.warehouseId,
        storageLocation: data?.article.storageLocation,
        handlingUnit: data?.article.handlingUnit,
        dispensingUnit: data?.article.dispensingUnit,
        supplierId: data?.article.supplierId,
      },
      receiveNoteItem: {
        quantity: defaultTo(1, data?.receiveNoteItem?.quantity),
        unitPrice: defaultTo(0, data?.receiveNoteItem?.purchasePrice),
        totalPrice: defaultTo(0, data?.receiveNoteItem?.purchaseValue),
        vatType: defaultTo('S', data?.receiveNoteItem?.vatType),
      },
      deliveryItem: {
        quantityVerified: defaultTo(1, data?.deliveryNoteItem?.quantityVerified),
        unitPrice: defaultTo(0, data?.deliveryNoteItem?.purchasePrice),
        totalPrice: defaultTo(0, data?.deliveryNoteItem?.purchaseValue),
        vatType: defaultTo('S', data?.deliveryNoteItem?.vatType),
        purchasePriceVerified: defaultTo(0, data?.deliveryNoteItem?.purchasePriceVerified),
        stockValueVerified: defaultTo(0, data?.deliveryNoteItem?.stockValueVerified),
      },
      supplierArticle: {
        supplierQuantity: data?.supplierArticle?.supplierQuantity,
        supplierUnitId: data?.supplierArticle?.supplierUnitId,
        warehouseQuantity: data?.supplierArticle?.warehouseQuantity,
        warehouseUnitId: data?.article?.handlingUnit,
        supplierOrderingNumber: data?.supplierArticle?.supplierOrderingNumber,
        supplierBulkPackageQuantity: data?.supplierArticle?.supplierBulkPackageQuantity,
        supplier: suppliers?.find((supplier) => supplier.id === data?.supplierArticle?.supplierId)
          ?.name,
        supplierDiscountGroup: data?.supplierArticle?.supplierDiscountGroupCode,
      },
    });
  };

  const resetAfterArticleReplacement = (data: GetReceiveNoteItemNewResponse | Nullish) => {
    const formApi = formApiRef?.current;

    if (isNil(formApi)) {
      return;
    }

    const currentValues = formApi.getValues();

    formApi.reset({
      ...currentValues,
      article: {
        ...currentValues.article,
        catalogNumber: data?.article?.manufacturerNumber,
        manufacturerId: data?.article?.manufacturerId,
        name: data?.article?.name,
        warehouseId: data?.article?.warehouseId,
        storageLocation: data?.article?.storageLocation,
        handlingUnit: data?.article?.handlingUnit,
        dispensingUnit: data?.article?.dispensingUnit,
      },
      supplierArticle: {
        ...currentValues.supplierArticle,
        supplierQuantity: data?.supplierArticle?.supplierQuantity,
        supplierUnitId: data?.supplierArticle?.supplierUnitId,
        warehouseQuantity: data?.supplierArticle?.warehouseQuantity,
        warehouseUnitId: data?.article?.handlingUnit,
        supplierOrderingNumber: data?.supplierArticle?.supplierOrderingNumber,
        supplierBulkPackageQuantity: data?.supplierArticle?.supplierBulkPackageQuantity,
        supplier: suppliers?.find((supplier) => supplier.id === data?.supplierArticle?.supplierId)
          ?.name,
        supplierDiscountGroup: data?.supplierArticle?.supplierDiscountGroupCode,
      },
    });
  };

  const handleChangeCategory = (activeFolderId: string | undefined) => {
    setLeafIdErrorMessage(undefined);
    setLeafId(activeFolderId);
  };

  const handleCreateNewArticle = (data: PostArticleRequest) => {
    createArticle(data)
      .unwrap()
      .then((postResult) => {
        handleGetReceiveNoteItem(postResult.articleId, true);
      })
      .catch(handleApiError);
  };

  const handleReplaceArticle = () => {
    const receiveNoteWarehouseId =
      receiveNoteItem?.article?.warehouseId ?? receiveNoteItemNew?.article?.warehouseId;
    const receiveNoteWarehouse =
      props.warehouse ?? warehouses?.find((warehouse) => warehouse.id === receiveNoteWarehouseId);

    const handleArticleReplaced = (articleId: string) => {
      setReplaceArticleId(articleId);
      closeDialog(REPLACE_ARTICLE_DIALOG);
    };

    const handleCreateArticleFromReplaceModal = (defaultValues: ArticleCreateDefaultValues) => {
      const REPLACE_ARTICLE_CREATE_ARTICLE_DIALOG = 'replace-article-create-article';

      const handleOnArticleCreate = (createdArticleData: CreatedArticleData) => {
        handleArticleReplaced(createdArticleData.articleId);
        closeDialog(REPLACE_ARTICLE_CREATE_ARTICLE_DIALOG);
      };

      openDialog(
        <ArticleCreate
          defaultValues={defaultValues}
          onArticleCreate={handleOnArticleCreate}
          onClose={() => closeDialog(REPLACE_ARTICLE_CREATE_ARTICLE_DIALOG)}
          isWarehouseDisabled
          hasCreateAndReplaceButton
          submitButtonTitle={i18n.t('entity.warehouse.labels.createAndReplaceArticle')}
          data-testid={suffixTestId('forms.createArticle', props)}
        />,
        {
          id: REPLACE_ARTICLE_CREATE_ARTICLE_DIALOG,
          title: i18n.t('entity.warehouse.labels.createNewArticle'),
          scrollBehavior: 'outside',
          withAdditionalFooter: true,
          'data-testid': suffixTestId('dialogs.createArticle', props),
        }
      );
    };

    openDialog(
      <ReplaceArticleModal
        authorizationProfileId={props.authorizationProfileId}
        tenant={props.tenant}
        warehouse={receiveNoteWarehouse}
        receiveNoteId={props.receiveNoteId}
        onArticleReplaced={handleArticleReplaced}
        onCreateArticleFromPricelist={handleCreateArticleFromReplaceModal}
        onCreateArticle={handleCreateArticleFromReplaceModal}
        data-testid={suffixTestId('replaceArticleModal', props)}
      />,
      {
        id: REPLACE_ARTICLE_DIALOG,
        'data-testid': suffixTestId('dialogs.replaceArticle', props),
      }
    );
  };

  const mapWarehouseUnit = (
    handlingUnit: string,
    formApi: UseFormReturn<ReceiveNoteItemFormType>
  ) => {
    formApi.setValue('supplierArticle.warehouseUnitId', handlingUnit);
  };

  const isLoading =
    isReceiveNoteItemLoading ||
    areSuppliersLoading ||
    isWarehouseLoading ||
    isDefaultTreeFolderLoading ||
    isLoadingHandlingUnits ||
    isDispensingUnitPresetsLoading;

  const segments = [
    {
      value: Segments.AVAILABILITY,
      label: i18n.t('entity.warehouse.labels.availability'),
      content: (
        <AvailabilitySegment
          articleId={replaceArticleId ?? articleId}
          manufacturerNumber={manufacturerNumber}
          manufacturerId={manufacturerId}
          data-testid={suffixTestId('segments.availability', props)}
        />
      ),
    },
    {
      value: Segments.REQUESTS,
      label: i18n.t('entity.warehouse.labels.requests'),
      content: (
        <RequestsSegment
          receiveNoteItemId={props.receiveNoteItemId}
          data-testid={suffixTestId('segments.requests', props)}
        />
      ),
    },
    {
      value: Segments.SUPPLIER_ORDERS,
      label: i18n.t('entity.warehouse.labels.supplierOrders'),
      content: (
        <SupplierOrdersSegment
          receiveNoteItemId={props.receiveNoteItemId}
          data-testid={suffixTestId('segments.supplierOrders', props)}
        />
      ),
    },
  ];

  const segmentContent = segments.find((segment) => segment.value === activeSegment)?.content;

  return (
    <DataStatus isLoading={isLoading} minHeight={100}>
      <Form<ReceiveNoteItemFormType>
        // We fetch item detail asynchronously in edit mode, therefore we need the key so the Form remounts properly after getting data.
        // If not editing (adding/creating), the form should not remount to properly react to resetFormValues calls.
        key={isEditing ? generateHashFromObjects(defaultValues) : undefined}
        experimentalZodSchema={ReceiveNoteItemDetailSchema(articleState)}
        defaultValues={defaultValues}
        onSubmit={onFormSubmitHandle}
        isDisabled={not(canEditReceiveNote)}
      >
        {(control, formApi) => {
          // We need to have a ref of formApi to be able to check for item on mount
          formApiRef.current = formApi;
          // For controlling recalculations of prices
          formApi.watch((data, test) => handleChange(data, test, formApi));

          const manufacturerId = formApi.watch('article.manufacturerId');
          const catalogNumber = formApi.watch('article.catalogNumber');
          const warehouseId = formApi.watch('article.warehouseId');

          const isSearchInfoFulfill =
            isNilOrEmpty(catalogNumber) ||
            isNilOrEmpty(warehouseId) ||
            isNilOrEmpty(manufacturerId);

          const isCurrentStateChecked =
            isNotNil(lastCheckedState) &&
            manufacturerId === lastCheckedState?.manufacturerId &&
            catalogNumber === lastCheckedState?.catalogNumber &&
            warehouseId === lastCheckedState?.warehouseId;

          const isCheckItemButtonDisabled = isSearchInfoFulfill || isCurrentStateChecked;

          const isCurrentStateInvalid = isEditing ? false : !isCurrentStateChecked;

          const isSubmitDisabled =
            props.isAddingToBasket ||
            doesNotExist ||
            isPatchReceiveNoteItemLoading ||
            isEditingDisabled ||
            isCurrentStateInvalid;

          return (
            <VStack>
              <VStack spacing={4}>
                <Show when={not(isEditing) && doesExist}>
                  <Alert
                    type="inline"
                    variant="info"
                    title={i18n.t('entity.warehouse.labels.itemIsAlreadyInDatabase')}
                    message={i18n.t('entity.warehouse.labels.itemDetailsPrefilled')}
                    data-testid={suffixTestId('alerts.itemInDatabase', props)}
                  />
                </Show>

                <Show when={not(isEditing) && doesNotExist}>
                  <Alert
                    type="inline"
                    variant="warning"
                    title={i18n.t('entity.warehouse.labels.itemNotInDatabase')}
                    message={i18n.t('entity.warehouse.labels.youCanCreateNewWarehouseArticle')}
                    data-testid={suffixTestId('alerts.itemNotInDatabase', props)}
                  />
                </Show>

                <Show when={not(isEditing) && hasBeenCreated}>
                  <Alert
                    type="inline"
                    variant="success"
                    title={i18n.t('entity.warehouse.labels.warehouseArticleWasSuccessfullyCreated')}
                    data-testid={suffixTestId('alerts.articleCreated', props)}
                  />
                </Show>

                <Grid columns={4} spacing={4}>
                  <GridItem span={2}>
                    <Tooltip
                      label={
                        isEditing
                          ? i18n.t('entity.warehouse.tooltips.catalogNumberDisabledDuringEditing')
                          : i18n.t('entity.warehouse.tooltips.catalogNumberDisabledWhileChecking')
                      }
                      isDisabled={!isCheckingForArticle && !isEditing}
                    >
                      <FormField
                        control={control}
                        name="article.catalogNumber"
                        type="text"
                        label={i18n.t('entity.warehouse.labels.catalogueNumber')}
                        isRequired
                        isDisabled={isCheckingForArticle || isEditing}
                        data-testid={suffixTestId('inputs.catalogNumber', props)}
                      />
                    </Tooltip>
                  </GridItem>
                  <GridItem span={1}>
                    <Tooltip
                      label={
                        isEditing
                          ? i18n.t('entity.warehouse.tooltips.manufacturerDisabledDuringEditing')
                          : i18n.t('entity.warehouse.tooltips.manufacturerDisabledWhileChecking')
                      }
                      isDisabled={!isCheckingForArticle && !isEditing}
                    >
                      <FormField
                        control={control}
                        name="article.manufacturerId"
                        type="choice"
                        label={i18n.t('entity.warehouse.labels.manufacturer')}
                        options={getOptionsFromManufacturers(manufacturers)}
                        isLoading={areManufacturersLoading}
                        isRequired
                        isDisabled={isCheckingForArticle || isEditing}
                        isNotClearable
                        menuInPortal
                        data-testid={suffixTestId('inputs.manufacturer', props)}
                      />
                    </Tooltip>
                  </GridItem>
                  <GridItem span={1}>
                    <Tooltip label={i18n.t('entity.warehouse.tooltips.disabled')}>
                      <FormField
                        isNotClearable
                        control={control}
                        name="article.warehouseId"
                        type="choice"
                        label={i18n.t('entity.warehouse.labels.warehouse')}
                        options={getOptionsFromWarehouses(warehouses)}
                        isRequired={isCreating}
                        isDisabled={true}
                        menuInPortal
                        data-testid={suffixTestId('inputs.warehouse', props)}
                      />
                    </Tooltip>
                  </GridItem>

                  <Hide when={isEditing}>
                    <GridItem span={1}>
                      <Tooltip
                        label={i18n.t('entity.warehouse.labels.currentStateIsChecked')}
                        isDisabled={!isCurrentStateChecked}
                      >
                        <Button
                          variant="secondary"
                          title={i18n.t('general.labels.checkItem')}
                          onClick={() =>
                            handleCheckItem({
                              manufacturerId,
                              catalogNumber,
                              warehouseId,
                            })
                          }
                          isLoading={isCheckingForArticle}
                          isDisabled={isCheckItemButtonDisabled}
                          data-testid={suffixTestId('actions.checkItem', props)}
                        />
                      </Tooltip>
                    </GridItem>
                  </Hide>
                </Grid>
              </VStack>

              <Separator />

              <DataStatus isLoading={isLoadingMainContent} minHeight={100}>
                <Hide when={doesNotExist}>
                  <ReceiveNoteItemForm
                    control={control}
                    handlingUnit={articleHandlingUnitName ?? defaultHandlingUnit?.name}
                    currency={currency}
                    isChecked={isChecked}
                    isEditingDisabled={isEditingDisabled}
                    isFromReceiveNote={props.isFromReceiveNote}
                    data-testid={suffixTestId('sections.receiveNoteItem', props)}
                  />
                  <Separator />
                  <DeliveryItemForm
                    control={control}
                    deliveryNoteQuantity={deliveryNoteQuantity}
                    deliveryNoteUnitPrice={deliveryNoteUnitPrice}
                    deliveryNoteTotalPrice={deliveryNoteTotalPrice}
                    handlingUnit={articleHandlingUnitName ?? defaultHandlingUnit?.name}
                    currency={currency}
                    isChecked={isChecked}
                    isEditingDisabled={isEditingDisabled}
                    isFromReceiveNote={props.isFromReceiveNote}
                    receiveNoteState={props.state}
                    receiveNoteType={props.receiveNoteType}
                    data-testid={suffixTestId('sections.deliveryItem', props)}
                  />
                  <Separator />
                  <SupplierArticleForm
                    control={control}
                    handlingUnits={handlingUnits}
                    isChecked={isChecked}
                    isEditingDisabled={isEditingDisabled}
                    supplierDiscountGroups={supplierDiscountGroups}
                    loadingState={{
                      isLoadingHandlingUnits,
                      isLoadingSupplierDiscountGroups,
                    }}
                    data-testid={suffixTestId('sections.supplierArticle', props)}
                  />
                  <Space vertical={4} />
                  <DeliveryItemDetailCard
                    deliveryItemDetail={receiveNoteItemDetails?.deliveryItemDetail}
                    deliveryNoteQuantity={deliveryNoteQuantity}
                    handlingUnit={articleHandlingUnitName ?? defaultHandlingUnit?.name}
                    isDisabled={isNotChecked}
                    isExpanded={shouldCardsBeExpanded}
                    isRefreshingDetails={isRefreshingDetails}
                    data-testid={suffixTestId('sections.deliveryItemDetailCard', props)}
                  />
                  <Space vertical={4} />
                  <ArticleDetailCard
                    articleDetail={receiveNoteItemDetails?.articleDetail}
                    currency={currency}
                    prices={articlePrices}
                    isDisabled={isNotChecked}
                    isExpanded={shouldCardsBeExpanded}
                    isRefreshingDetails={isRefreshingDetails}
                    onRefresh={() => handleRefreshArticle(formApi)}
                    onReplaceArticle={() => handleReplaceArticle()}
                    warehouseName={warehouseName}
                    isArticleReplacement={isArticleReplacement}
                    data-testid={suffixTestId('sections.articleDetailCard', props)}
                  />
                  <Space vertical={4} />
                </Hide>

                <Show when={isEditing}>
                  <HStack>
                    <Segment<Segments>
                      value={activeSegment}
                      onChange={setActiveSegment}
                      options={segments}
                    />
                  </HStack>
                  <Box>{segmentContent}</Box>
                </Show>

                <DialogFooter>
                  <HStack>
                    <Show when={isArticleReplaced || isNotNil(replaceArticleId)}>
                      <HStack align="center">
                        <Flag
                          isSubtle
                          colorScheme="yellow"
                          label={i18n.t('entity.warehouse.labels.replaceArticle')}
                        />
                        <Space horizontal={2} />
                        <Label tooltip={i18n.t('general.labels.replacedArticle')}>
                          {i18n.t('entity.warehouse.labels.receiveItemHasBeenReplacedManually')}
                        </Label>
                      </HStack>
                    </Show>
                    <Space fillAvailable />
                    <ButtonGroup align="right">
                      <Button
                        variant="secondary"
                        title={i18n.t('general.labels.discard')}
                        onClick={() => closeCurrentDialog()}
                        data-testid={suffixTestId('actions.discard', props)}
                      />
                      <FormButton
                        isDisabled={isSubmitDisabled || isCalculateLoading}
                        control={control}
                        type="submit"
                        variant="primary"
                        title={
                          isEditing
                            ? i18n.t('general.labels.saveChanges')
                            : i18n.t('general.labels.add')
                        }
                        data-testid={suffixTestId('actions.submit', props)}
                      />
                    </ButtonGroup>
                  </HStack>
                </DialogFooter>

                <Show when={doesNotExist}>
                  <VStack spacing={4} data-testid={props['data-testid']}>
                    <Card
                      variant="inlineGrey"
                      title={i18n.t('entity.warehouse.labels.articleDetail')}
                    >
                      <Grid columns={4}>
                        <GridItem span={2}>
                          <FormField
                            type="text"
                            name="article.catalogNumber"
                            label={i18n.t('entity.warehouse.labels.catalogueNumber')}
                            control={control}
                            isRequired
                            data-testid={suffixTestId('inputs.catalogNumber', props)}
                          />
                        </GridItem>
                        <GridItem span={2}>
                          <FormField
                            type="choice"
                            name="article.manufacturerId"
                            label={i18n.t('entity.warehouse.labels.manufacturer')}
                            options={getOptionsFromManufacturers(manufacturers)}
                            control={control}
                            isRequired
                            isNotClearable
                            menuInPortal
                            data-testid={suffixTestId('inputs.manufacturer', props)}
                          />
                        </GridItem>
                        <GridItem span={2}>
                          <FormField
                            type="text"
                            name="article.name"
                            label={i18n.t('entity.warehouse.labels.name')}
                            control={control}
                            isRequired
                            data-testid={suffixTestId('inputs.name', props)}
                          />
                        </GridItem>
                        <GridItem span={2}>
                          {/* Warehouse account field will go here in the future */}
                          <div />
                        </GridItem>
                        <GridItem span={2}>
                          <FormField
                            type="text"
                            name="article.storageLocation"
                            label={i18n.t('entity.warehouse.labels.storageLocation')}
                            control={control}
                            data-testid={suffixTestId('inputs.storageLocation', props)}
                          />
                        </GridItem>
                        <GridItem span={1}>
                          <FormField
                            isRequired
                            isNotClearable
                            isLoading={isLoadingHandlingUnits}
                            type="choice"
                            name="article.handlingUnit"
                            label={i18n.t('entity.warehouse.labels.unit')}
                            options={getOptionsFromHandlingUnits(handlingUnits)}
                            control={control}
                            menuInPortal
                            data-testid={suffixTestId('inputs.unit', props)}
                          />
                        </GridItem>
                        <GridItem span={1}>
                          <FormField
                            control={control}
                            name="article.dispensingUnit"
                            type="number"
                            label={i18n.t('entity.warehouse.labels.dispensingUnit')}
                            labelActions={
                              dispensingUnitPresets
                                ? [
                                    {
                                      title: i18n.t('entity.warehouse.labels.presets'),
                                      onClick: (e) =>
                                        openDropdown(
                                          e,
                                          dispensingUnitPresets?.map((preset) => (
                                            <DropdownItem
                                              key={preset.id}
                                              label={preset.value.toString()}
                                              onClick={() =>
                                                formApi.setValue(
                                                  'article.dispensingUnit',
                                                  preset.value
                                                )
                                              }
                                            />
                                          ))
                                        ),
                                    },
                                  ]
                                : undefined
                            }
                            decimalPlaces={4}
                            data-testid={suffixTestId('dispensingUnit', props)}
                          />
                        </GridItem>
                        <GridItem span={4}>
                          <ArticleCreateTreeFolderPath
                            leafId={leafId}
                            onChange={(activeFolder) => handleChangeCategory(activeFolder?.id)}
                            invalidMessage={leafIdErrorMessage}
                            isRequired
                            data-testid={suffixTestId('inputs.category', props)}
                          />
                        </GridItem>
                      </Grid>
                    </Card>
                    <HStack>
                      <Tooltip
                        label={i18n.t('entity.warehouse.labels.firstlyCheckItem')}
                        isDisabled={isCurrentStateChecked}
                      >
                        <Button
                          isLoading={isCreateArticleLoading}
                          isDisabled={!isCurrentStateChecked || isCalculateLoading}
                          type="submit"
                          variant="link"
                          size="small"
                          title={i18n.t('entity.warehouse.actions.createNewArticle')}
                          leftIcon="content/add_circle"
                          data-testid={suffixTestId('actions.createNewArticle', props)}
                        />
                      </Tooltip>
                    </HStack>
                  </VStack>
                </Show>
              </DataStatus>
            </VStack>
          );
        }}
      </Form>
    </DataStatus>
  );
}
